/**
 * Retry utility with exponential backoff and circuit breaker pattern
 * For production-ready error handling and resilience
 */

import { logger } from '../services/logger';

export interface RetryOptions {
  maxRetries?: number;
  delay?: number;
  backoff?: 'linear' | 'exponential';
  shouldRetry?: (error: any) => boolean;
  onRetry?: (attempt: number, error: any) => void;
}

/**
 * Retries a function with exponential backoff
 */
export async function retry<T>(fn: () => Promise<T>, options: RetryOptions = {}): Promise<T> {
  const {
    maxRetries = 3,
    delay = 1000,
    backoff = 'exponential',
    shouldRetry = () => true,
    onRetry,
  } = options;

  let lastError: any;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;

      // Don't retry if we've exhausted attempts or shouldRetry returns false
      if (attempt === maxRetries || !shouldRetry(error)) {
        throw error;
      }

      // Calculate wait time with backoff
      const waitTime =
        backoff === 'exponential' ? delay * Math.pow(2, attempt) : delay * (attempt + 1);

      if (onRetry) {
        onRetry(attempt + 1, error);
      }

      logger.info(
        `Attempt ${attempt + 1}/${maxRetries} failed, retrying in ${waitTime}ms...`,
        { attempt, waitTime },
        'Retry'
      );
      await new Promise((resolve) => setTimeout(resolve, waitTime));
    }
  }

  throw lastError;
}

/**
 * Circuit Breaker pattern for preventing cascading failures
 */
export class CircuitBreaker {
  private failures = 0;
  private lastFailureTime = 0;
  private state: 'closed' | 'open' | 'half-open' = 'closed';

  constructor(
    private threshold = 5,
    private timeout = 60000, // 1 minute
    private name = 'CircuitBreaker'
  ) {}

  async execute<T>(fn: () => Promise<T>): Promise<T> {
    if (this.state === 'open') {
      if (Date.now() - this.lastFailureTime > this.timeout) {
        logger.info(`Transitioning to HALF-OPEN state`, undefined, `CircuitBreaker:${this.name}`);
        this.state = 'half-open';
      } else {
        throw new Error(`Circuit breaker is OPEN (${this.name}). Too many failures.`);
      }
    }

    try {
      const result = await fn();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  private onSuccess() {
    if (this.state === 'half-open') {
      logger.info(
        `Success in HALF-OPEN, closing circuit`,
        undefined,
        `CircuitBreaker:${this.name}`
      );
    }
    this.failures = 0;
    this.state = 'closed';
  }

  private onFailure() {
    this.failures++;
    this.lastFailureTime = Date.now();

    if (this.failures >= this.threshold) {
      logger.error(
        `Opening circuit after ${this.failures} failures`,
        undefined,
        `CircuitBreaker:${this.name}`
      );
      this.state = 'open';
    }
  }

  getState(): 'closed' | 'open' | 'half-open' {
    return this.state;
  }

  reset() {
    this.failures = 0;
    this.state = 'closed';
    this.lastFailureTime = 0;
  }
}

/**
 * Rate limiter to prevent overwhelming the database
 */
export class RateLimiter {
  private requests: number[] = [];

  constructor(
    private maxRequests: number,
    private windowMs: number,
    private name = 'RateLimiter'
  ) {}

  async check(): Promise<void> {
    const now = Date.now();
    // Remove old requests outside the window
    this.requests = this.requests.filter((time) => now - time < this.windowMs);

    if (this.requests.length >= this.maxRequests) {
      const oldestRequest = this.requests[0];
      const waitTime = this.windowMs - (now - oldestRequest);

      if (waitTime > 0) {
        logger.info(
          `Rate limit reached, waiting ${waitTime}ms`,
          { waitTime },
          `RateLimiter:${this.name}`
        );
        await new Promise((resolve) => setTimeout(resolve, waitTime));
        return this.check(); // Recursive check after waiting
      }
    }

    this.requests.push(now);
  }

  reset() {
    this.requests = [];
  }
}

// Global instances
export const dbCircuitBreaker = new CircuitBreaker(5, 60000, 'Database');
export const dbRateLimiter = new RateLimiter(10, 1000, 'Database'); // 10 req/sec

/**
 * Helper to determine if an error should be retried
 */
export function shouldRetryDatabaseError(error: any): boolean {
  // Don't retry authentication/permission errors
  if (error?.code === 'PGRST301' || error?.code === '42501') {
    return false;
  }

  // Don't retry validation errors
  if (error?.code === '23505' || error?.code === '23503') {
    return false;
  }

  // Retry network/timeout errors
  const message = error?.message?.toLowerCase() || '';
  if (
    message.includes('timeout') ||
    message.includes('network') ||
    message.includes('fetch') ||
    message.includes('connection')
  ) {
    return true;
  }

  // Retry server errors (5xx)
  if (error?.status >= 500) {
    return true;
  }

  return false;
}
